import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, Input, Layout, Modal, Popconfirm, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import organizationService from '../services/organizationService';
import roleService from '../services/roleService';
import { canManageRoles, isSuperAdmin } from '../utils/permissions';

const BASE_ACTIONS = [
  { key: 'create', short: 'C', label: 'Create' },
  { key: 'read', short: 'R', label: 'Read' },
  { key: 'update', short: 'U', label: 'Update' },
  { key: 'delete', short: 'D', label: 'Delete' }
];

const LEAVE_ACTIONS = [
  { key: 'approve', short: 'A', label: 'Approve' },
  { key: 'reject', short: 'R', label: 'Reject' }
];

const PAYSLIP_ACTIONS = [
  { key: 'generate', short: 'G', label: 'Generate' },
  { key: 'process', short: 'P', label: 'Process' },
  { key: 'download', short: 'D', label: 'Download' }
];

const PERMISSION_MODULES = [
  { key: 'organization', label: 'Organization' },
  { key: 'roles', label: 'Roles' },
  { key: 'users', label: 'Users' },
  { key: 'employees', label: 'Employees' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'leaves', label: 'Leaves' },
  { key: 'holidays', label: 'Holidays' },
  { key: 'salaryStructure', label: 'Salary Structure' },
  { key: 'payroll', label: 'Payroll' },
  { key: 'payslipTemplates', label: 'Payslip Templates' },
  { key: 'fiscalYear', label: 'Fiscal Year' },
  { key: 'settings', label: 'Settings' },
  { key: 'leaveTypes', label: 'Leave Types' },
  { key: 'documents', label: 'Documents' },
  { key: 'ctc', label: 'CTC' },
  { key: 'taxDeclaration', label: 'Tax Declaration' }
];

const PERMISSION_ALIASES = {
  payslipTemplates: ['payslipTemplate', 'payslipStructure', 'payslip_structure'],
  salaryStructure: ['payrollStructure', 'salary_structure', 'payslipStructure', 'payslip_structure']
};

const getActionsForModule = (moduleKey) => {
  if (moduleKey === 'leaves') {
    return [...BASE_ACTIONS, ...LEAVE_ACTIONS];
  }
  if (moduleKey === 'payroll') {
    return [...BASE_ACTIONS, ...PAYSLIP_ACTIONS];
  }
  return BASE_ACTIONS;
};

const createEmptyPermissions = () =>
  PERMISSION_MODULES.reduce((acc, module) => {
    const actions = getActionsForModule(module.key);
    acc[module.key] = actions.reduce((actionAcc, action) => {
      actionAcc[action.key] = false;
      return actionAcc;
    }, {});
    return acc;
  }, {});

const normalizePermissions = (permissions) => {
  if (!permissions || Array.isArray(permissions)) {
    return createEmptyPermissions();
  }

  const normalized = createEmptyPermissions();

  PERMISSION_MODULES.forEach((module) => {
    const actions = getActionsForModule(module.key);
    actions.forEach((action) => {
      const possibleKeys = [module.key, ...(PERMISSION_ALIASES[module.key] || [])];
      normalized[module.key][action.key] = possibleKeys.some((key) => Boolean(permissions?.[key]?.[action.key]));
    });
  });

  return normalized;
};

function Roles() {
  const [form] = Form.useForm();
  const { user } = useSelector((state) => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(user?.organizationId || undefined);

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId),
    [selectedOrganizationId, user]
  );

  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isSuperAdmin(user)) {
        return;
      }

      try {
        const response = await organizationService.getAll(1, 1000);
        setOrganizations(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organizations');
      }
    };

    loadOrganizations();
  }, [user]);

  useEffect(() => {
    const loadRoles = async () => {
      setLoading(true);
      try {
        const response = await roleService.getAll(1, 100, effectiveOrganizationId ? { organizationId: effectiveOrganizationId } : {});
        setRoles(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadRoles();
    }
  }, [effectiveOrganizationId, user]);

  const openCreateModal = () => {
    setIsEditMode(false);
    setSelectedRole(null);
    form.setFieldsValue({
      organizationId: isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId,
      name: '',
      description: '',
      permissions: createEmptyPermissions()
    });
    setIsModalOpen(true);
  };

  const openEditModal = (role) => {
    setIsEditMode(true);
    setSelectedRole(role);
    form.setFieldsValue({
      organizationId: role.organizationId,
      name: role.name,
      description: role.description,
      permissions: normalizePermissions(role.permissions)
    });
    setIsModalOpen(true);
  };

  const handleSaveRole = async (values) => {
    setLoading(true);
    try {
      const payload = {
        organizationId: isSuperAdmin(user) ? values.organizationId : user?.organizationId,
        name: values.name,
        description: values.description || '',
        permissions: values.permissions || createEmptyPermissions()
      };

      if (isEditMode && selectedRole?.id) {
        const response = await roleService.update(selectedRole.id, payload);
        setRoles((prev) => prev.map((role) => (role.id === selectedRole.id ? response.data : role)));
        message.success('Role updated successfully');
      } else {
        const response = await roleService.create(payload);
        setRoles((prev) => [response.data, ...prev]);
        message.success('Role created successfully');
      }

      form.resetFields();
      setSelectedRole(null);
      setIsEditMode(false);
      setIsModalOpen(false);
    } catch (error) {
      message.error(error.response?.data?.message || (isEditMode ? 'Failed to update role' : 'Failed to create role'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    setLoading(true);
    try {
      await roleService.delete(roleId);
      setRoles((prev) => prev.filter((role) => role.id !== roleId));
      message.success('Role deleted successfully');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  const renderReadonlyPermission = (role, moduleKey, actionKey) => {
    const normalized = normalizePermissions(role.permissions);
    const checked = normalized[moduleKey][actionKey];
    const actions = getActionsForModule(moduleKey);

    return (
      <div className={`roles-matrix__cell ${checked ? 'roles-matrix__cell--active' : ''}`}>
        <Checkbox checked={checked} disabled />
        <span>{actions.find((action) => action.key === actionKey)?.short}</span>
      </div>
    );
  };

  return (
    <Layout style={{ padding: '20px' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Typography.Title level={1} style={{ marginBottom: 4 }}>Roles & Permissions</Typography.Title>
            <Typography.Text style={{ color: '#667085' }}>
              Configure organization-specific role permissions with a module-wise CRUD matrix.
            </Typography.Text>
          </div>
          {canManageRoles(user) && (
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openCreateModal}>
              Add Role
            </Button>
          )}
        </div>

        {!canManageRoles(user) && (
          <Alert type="info" message="This role does not have access to manage roles." />
        )}

        <Card bodyStyle={{ padding: 0 }}>
          <Space direction="vertical" size={16} style={{ width: '100%', padding: 20 }}>
            {isSuperAdmin(user) && (
              <Select
                allowClear
                placeholder="Filter by organization"
                value={selectedOrganizationId}
                onChange={setSelectedOrganizationId}
                options={organizations.map((organization) => ({
                  label: organization.name,
                  value: organization.id
                }))}
                style={{ width: 320 }}
              />
            )}
          </Space>

          <Spin spinning={loading}>
            <div className="roles-matrix">
              <div className="roles-matrix__scroll">
                <table className="roles-matrix__table">
                  <thead>
                    <tr>
                      <th className="roles-matrix__role-col">Role</th>
                      {PERMISSION_MODULES.map((module) => (
                        <th key={module.key} className="roles-matrix__module-col">
                          {module.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => (
                      <tr key={role.id}>
                        <td className="roles-matrix__role-cell">
                          <div className="roles-matrix__role-name">{role.name}</div>
                          <div className="roles-matrix__role-meta">
                            <Tag color={role.roleType === 'Custom' ? 'blue' : 'default'}>{role.roleType}</Tag>
                            <Tag color={role.isActive ? 'green' : 'red'}>{role.isActive ? 'Active' : 'Inactive'}</Tag>
                            {canManageRoles(user) && role.organizationId && (
                              <Space size="small">
                                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(role)}>
                                  Edit
                                </Button>
                                <Popconfirm
                                  title="Delete role"
                                  description={`Are you sure you want to delete ${role.name}?`}
                                  onConfirm={() => handleDeleteRole(role.id)}
                                  okText="Delete"
                                  cancelText="Cancel"
                                >
                                  <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                                    Delete
                                  </Button>
                                </Popconfirm>
                              </Space>
                            )}
                          </div>
                        </td>
                        {PERMISSION_MODULES.map((module) => {
                          const actions = getActionsForModule(module.key);
                          return (
                            <td key={module.key} className="roles-matrix__module-cell">
                              <div className="roles-matrix__crud-grid">
                                {actions.map((action) => (
                                  <div key={`${role.id}-${module.key}-${action.key}`}>
                                    {renderReadonlyPermission(role, module.key, action.key)}
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {!roles.length && (
                      <tr>
                        <td colSpan={PERMISSION_MODULES.length + 1} className="roles-matrix__empty">
                          No roles found for the selected organization.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Spin>
        </Card>
      </Space>

      <Modal
        title={isEditMode ? 'Edit Role' : 'Create Role'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setIsEditMode(false);
          setSelectedRole(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={isEditMode ? 'Update Role' : 'Create Role'}
        confirmLoading={loading}
        width={1100}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveRole}
          initialValues={{
            organizationId: isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId,
            permissions: createEmptyPermissions()
          }}
        >
          {isSuperAdmin(user) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select organization' }]}
            >
              <Select
                placeholder="Select organization"
                options={organizations.map((organization) => ({
                  label: organization.name,
                  value: organization.id
                }))}
                onChange={setSelectedOrganizationId}
              />
            </Form.Item>
          )}

          <div className="roles-matrix__form-grid">
            <Form.Item label="Role Name" name="name" rules={[{ required: true, message: 'Please enter role name' }]}>
              <Input placeholder="e.g. HR Executive" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input placeholder="Describe what this role can manage" />
            </Form.Item>
          </div>

          <div className="roles-matrix roles-matrix--editor">
            <div className="roles-matrix__scroll">
              <table className="roles-matrix__table">
                <thead>
                  <tr>
                    <th className="roles-matrix__role-col">Module</th>
                    {BASE_ACTIONS.concat(LEAVE_ACTIONS).map((action) => (
                      <th key={action.key} className="roles-matrix__action-col">
                        {action.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_MODULES.map((module) => {
                    const actions = getActionsForModule(module.key);
                    return (
                      <tr key={module.key}>
                        <td className="roles-matrix__role-cell roles-matrix__role-cell--module">
                          {module.label}
                        </td>
                        {actions.map((action) => (
                          <td key={`${module.key}-${action.key}`} className="roles-matrix__module-cell">
                            <Form.Item
                              name={['permissions', module.key, action.key]}
                              valuePropName="checked"
                              noStyle
                            >
                              <Checkbox className="roles-matrix__editor-checkbox">
                                {action.short}
                              </Checkbox>
                            </Form.Item>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Form>
      </Modal>
    </Layout>
  );
}

export default Roles;
